import { VocalLib, intervalName, withArticle } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useEffect, useRef } from 'react'
import { useVocalCopy } from '../pitch-training/use-vocal-copy.js'
import { TheoryGlyphs } from '../theory-copy/theory-glyphs.js'
import {
  midBarCut,
  midBarLabels,
  noteLabel,
  prepareTarget,
  type PreparedTarget,
  type Singable,
} from './practice-target.js'
import styles from './phrase-refusal.module.css'

/**
 * Wider than your range (Pitch Trainer Phase 2, plan M6a step 6.5; Joni §4), shown before any audio plays.
 * It places the failure in the data and gives a way forward: the halves of this phrase first, since the
 * singer chose it as the bit they keep missing, focused on the first that fits. Never an alert: the heading
 * carries the state.
 */

export interface PhraseRefusalProps {
  refused: Extract<PreparedTarget, { kind: 'refused' }>
  range: VocalLib.VocalRange
  onSing: (half: Singable) => void
  /** Neither half fits: back to the picker, on the next Suggested phrase that fits */
  onPickAnother: () => void
  /** Calibration, then straight back to this phrase */
  onMeasureRange: () => void
  onCancel: () => void
}

const sharpNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B']
const rangeName = (midi: number) => noteLabel(sharpNames[((midi % 12) + 12) % 12], midi)

/** Joni, M6a: halves cut inside a bar say where the cut falls, so no bar reads as sung twice */
const halfLabels = (halves: Singable[]): string[] => {
  const [first, second] = halves
  if (halves.length === 2 && first?.kind === 'phrase' && second?.kind === 'phrase') {
    const onset = second.phrase.events[0]?.onset
    const cut = onset === undefined ? undefined : midBarCut(second.source.melody.tempo.meter, onset)
    if (cut) {
      return midBarLabels(cut).halves
    }
  }
  return halves.map(halfLabel)
}

const halfLabel = (half: Singable) => {
  if (half.kind !== 'phrase') {
    const { first, last } = half.part.lines
    return first === last ? `Line ${first}` : `Lines ${first}–${last}`
  }
  const { startBar, endBar } = half.phrase.source
  return startBar === endBar ? `Bar ${startBar}` : `Bars ${startBar}–${endBar}`
}

export const PhraseRefusal: FC<PhraseRefusalProps> = props => {
  const { refused, range, onSing, onPickAnother, onMeasureRange, onCancel } = props
  const headingRef = useRef<HTMLHeadingElement>(null)
  const firstFitRef = useRef<HTMLButtonElement>(null)
  const pickRef = useRef<HTMLButtonElement>(null)
  const span = intervalName(refused.spanSemitones)
  const copy = useVocalCopy('vocal-phrase-out-of-range', {
    span,
    aSpan: withArticle(span),
    low: rangeName(range.low),
    high: rangeName(range.high),
  })
  const labels = halfLabels(refused.halves)
  const halves = refused.halves.map((half, index) => ({
    half,
    label: labels[index] ?? halfLabel(half),
    fits: prepareTarget(half, range).kind === 'ready',
  }))
  const firstFit = halves.findIndex(entry => entry.fits)

  useEffect(() => {
    /** Joni §4: on the first half that fits, else on the way back to the picker */
    const target = firstFitRef.current ?? pickRef.current ?? headingRef.current
    target?.focus()
  }, [])

  return (
    <section className={styles.panel} aria-labelledby="phrase-refusal-heading">
      <h2 id="phrase-refusal-heading" ref={headingRef} tabIndex={-1} className={styles.title}>
        Wider than your range
      </h2>
      {copy ? (
        <p className={styles.body}>
          <TheoryGlyphs>{copy.body}</TheoryGlyphs>
        </p>
      ) : null}

      {halves.length > 0 ? (
        <div className={styles.halves} role="group" aria-label="Halves of this phrase">
          {halves.map(({ half, label, fits }, index) => (
            <Button
              key={label}
              ref={index === firstFit ? firstFitRef : undefined}
              variant={index === firstFit ? 'contained' : 'outlined'}
              disabled={!fits}
              onClick={() => onSing(half)}
            >
              {label}
            </Button>
          ))}
        </div>
      ) : null}

      <div className={styles.actions}>
        {firstFit === -1 ? (
          <Button ref={pickRef} variant="contained" onClick={onPickAnother}>
            Pick a shorter phrase
          </Button>
        ) : null}
        <Button variant="outlined" onClick={onMeasureRange}>
          Measure my range again
        </Button>
        <Button variant="text" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </section>
  )
}
